// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import GroupAddIcon from '@mui/icons-material/GroupAdd'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNotify, usePermissions, useTranslate } from 'react-admin'

import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders, EMAIL_RE } from '../utils/api'

/**
 * First-session onboarding prompt (case 2a of the pet-share refinement):
 * offer a newly registered vet to invite colleagues by email, sharing all
 * their pets onward via the existing email-invite flow
 * (POST /api/vet-shares/create, shareType=all_pets).
 *
 * Shown only when ALL hold:
 *  - role is vet (gated before any fetch)
 *  - firstLoginAt is recent (<24h) → this is effectively their first session
 *  - user.settings.onboardingSharePromptDone is not set
 *
 * Dismissal/submission persists the flag into user.settings (server-side —
 * survives browser/device changes; settings is in the self-update
 * allowlist). The flag is merged into existing settings, never clobbered.
 */

const FIRST_SESSION_WINDOW_MS = 24 * 60 * 60 * 1000
const DEFAULT_PERMISSIONS = ['view_metrics', 'view_emrs', 'add_activities', 'add_emr_notes']

// authProvider caches the full /api/users/me payload in sessionStorage
// ('identity_cache', { data, timestamp }, 5-min TTL). Reading it avoids an
// extra /me round trip on every dashboard mount — the prompt is mounted on
// the app's hottest page and shows for almost nobody.
const IDENTITY_CACHE_TTL = 5 * 60 * 1000
const readCachedUser = (): any | null => {
  try {
    const raw = sessionStorage.getItem('identity_cache')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.data?.user || Date.now() - parsed.timestamp > IDENTITY_CACHE_TTL) return null
    return parsed.data.user
  } catch (_e) {
    return null
  }
}

const OnboardingSharePrompt = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const { permissions } = usePermissions()

  const [open, setOpen] = useState(false)
  const [emails, setEmails] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [results, setResults] = useState<{ sent: number; failed: string[] } | null>(null)

  const isVet = permissions?.role === 'vet'

  // Persist the done-flag. Settings is a wholesale-replaced json field, so a
  // stale snapshot would clobber concurrent writes (e.g. the glucose-unit
  // switcher) — fetch FRESH settings immediately before the PATCH.
  const persistDone = async (userId: number, fallbackSettings: Record<string, unknown>) => {
    try {
      let settings = fallbackSettings
      try {
        const res = await deadlineFetch('/api/users/me', { headers: authHeaders() })
        if (res.ok) {
          const fresh = (await res.json())?.user
          if (fresh?.settings) settings = fresh.settings as Record<string, unknown>
        }
      } catch (_e) {
        // fall back to the snapshot
      }
      await deadlineFetch(`/api/users/${userId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          settings: { ...settings, onboardingSharePromptDone: true },
        }),
      })
      // Write the flag back into the cached identity too — the gating above
      // reads cache-first, so without this the prompt re-shows on reload for
      // up to the cache TTL even though the server flag is set (found live
      // on beta with a fresh vet account).
      try {
        const raw = sessionStorage.getItem('identity_cache')
        if (raw) {
          const parsed = JSON.parse(raw)
          if (parsed?.data?.user) {
            parsed.data.user.settings = {
              ...(parsed.data.user.settings || {}),
              onboardingSharePromptDone: true,
            }
            sessionStorage.setItem('identity_cache', JSON.stringify(parsed))
          }
        }
      } catch (_e) {
        // cache update is best-effort
      }
    } catch (_e) {
      // Best-effort; worst case the prompt shows again on the next mount
    }
  }

  useEffect(() => {
    if (!isVet) return
    let cancelled = false

    const evaluate = (user: any) => {
      const settings = (user.settings as Record<string, unknown>) || {}
      const firstLogin = user.firstLoginAt ? new Date(user.firstLoginAt).getTime() : 0
      const isFirstSession = firstLogin > 0 && Date.now() - firstLogin < FIRST_SESSION_WINDOW_MS
      if (isFirstSession && !settings.onboardingSharePromptDone) {
        setOpen(true)
        // Mark done the moment it is SHOWN: deterministic shows-exactly-once.
        // Without this, a vet who closes the tab re-sees the prompt on every
        // load inside the 24h window, then silently loses it forever after.
        void persistDone(user.id, settings)
      }
    }

    // Cache first — only fall back to the network when the identity cache is
    // missing or stale.
    const cached = readCachedUser()
    if (cached) {
      evaluate(cached)
      return
    }
    ;(async () => {
      try {
        const res = await deadlineFetch('/api/users/me', { headers: authHeaders() })
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled && data?.user) evaluate(data.user)
      } catch (_e) {
        // Silent: the prompt is best-effort, never block the dashboard
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isVet])

  const handleSkip = () => {
    setOpen(false)
  }

  const handleSubmit = async () => {
    setError('')
    const list = emails
      .split(/[\s,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean)
    if (list.length === 0 || list.some((e) => !EMAIL_RE.test(e))) {
      setError(translate('pos.onboarding_share.error_invalid'))
      return
    }

    setSubmitting(true)
    const failed: string[] = []
    let sent = 0
    for (const email of list) {
      try {
        const res = await deadlineFetch('/api/vet-shares/create', {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            shareType: 'all_pets',
            permissions: DEFAULT_PERMISSIONS,
            recipientEmail: email,
          }),
        })
        if (res.ok) {
          sent += 1
        } else {
          failed.push(email)
        }
      } catch (_e) {
        failed.push(email)
      }
    }
    setSubmitting(false)
    setResults({ sent, failed })
    if (failed.length === 0) {
      notify('pos.shares.share_created_successfully', { type: 'success' })
    }
  }

  if (!open) return null

  return (
    <Dialog open={open} onClose={submitting ? undefined : handleSkip} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GroupAddIcon color="primary" />
        {translate('pos.onboarding_share.title')}
      </DialogTitle>
      <DialogContent>
        {results ? (
          <Box>
            <Alert severity={results.failed.length ? 'warning' : 'success'} sx={{ mb: 1 }}>
              {translate('pos.onboarding_share.done', { count: results.sent })}
            </Alert>
            {results.failed.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {translate('pos.onboarding_share.failed_list', {
                  emails: results.failed.join(', '),
                })}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {translate('pos.onboarding_share.subtitle')}
            </Typography>
            <TextField
              label={translate('pos.onboarding_share.emails')}
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder={translate('pos.onboarding_share.emails_placeholder')}
              fullWidth
              multiline
              minRows={2}
              disabled={submitting}
            />
            <Typography variant="caption" color="text.secondary">
              {translate('pos.onboarding_share.hint')}
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {results ? (
          <Button variant="contained" onClick={() => setOpen(false)}>
            {translate('ra.action.close')}
          </Button>
        ) : (
          <>
            <Button onClick={handleSkip} disabled={submitting}>
              {translate('pos.onboarding_share.skip')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : undefined}
            >
              {translate('pos.onboarding_share.submit')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default OnboardingSharePrompt
